import {
  useState,
  useMemo,
  useRef,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';

import { Icons } from '@/assets/icons/icons';
import {
  DropdownMenuRoot,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu/dropdown-menu';
import { Input } from '@/components/ui/Input';
import { afterFrames } from '@/lib/animate';
import { classList } from '@/utils/cn';

export interface SearchableDropdownOption {
  value: string;
  label: string;
  description?: string;
}

interface SearchableDropdownProps {
  options: SearchableDropdownOption[];
  value?: string;
  placeholder?: string;
  searchPlaceholder?: string;
  onValueChange: (value: string) => void;
  triggerClassName?: string;
  contentClassName?: string;
  disabled?: boolean;
  maxHeight?: string;
  emptyResult?: ReactNode | string;
}

export function SearchableDropdown({
  options,
  value,
  placeholder = 'Select option...',
  searchPlaceholder = 'Search...',
  onValueChange,
  triggerClassName,
  contentClassName,
  disabled = false,
  maxHeight = 'max-h-60',
  emptyResult = 'No options found',
}: SearchableDropdownProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [showTopFade, setShowTopFade] = useState(false);
  const [showBottomFade, setShowBottomFade] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const dropdownContentRef = useRef<HTMLDivElement>(null);

  // Filter options based on search term
  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return options;

    const searchLower = searchTerm.toLowerCase();
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(searchLower) ||
        option.value.toLowerCase().includes(searchLower)
    );
  }, [searchTerm, options]);

  // Set selected index to current value when filtered options change
  useEffect(() => {
    const currentOptionIndex = filteredOptions.findIndex(
      (option) => option.value === value
    );
    setSelectedIndex(currentOptionIndex);
  }, [filteredOptions, value]);

  const handleValueChange = (newValue: string) => {
    onValueChange(newValue);
    setIsDropdownOpen(false);
    setSearchTerm('');
    setSelectedIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!filteredOptions.length) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < filteredOptions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev > 0 ? prev - 1 : filteredOptions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < filteredOptions.length) {
          handleValueChange(filteredOptions[selectedIndex].value);
        }
        break;
      case 'Escape':
        setIsDropdownOpen(false);
        break;
      default:
        // If it's a character key and search input exists, focus it
        if (e.key.length === 1) {
          afterFrames(2, () => {
            searchInputRef.current?.focus();
          });
        }
        break;
    }
    e.stopPropagation();
  };

  const handleDropdownOpenChange = (open: boolean) => {
    setIsDropdownOpen(open);
    if (!open) {
      setSearchTerm('');
    } else {
      // Focus the search input when dropdown opens
      afterFrames(2, () => {
        searchInputRef.current?.focus();
      });
    }
  };

  // Check scroll position and update fade states
  const updateScrollFades = useCallback((e?: React.UIEvent) => {
    // Stop scroll event from bubbling up
    if (e) {
      e.stopPropagation();
    }

    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const { scrollTop, scrollHeight, clientHeight } = container;

      setShowTopFade(scrollTop > 0);
      setShowBottomFade(scrollTop < scrollHeight - clientHeight - 1);
    }
  }, []);

  // Update fades when dropdown opens and options change
  // useEffect(() => {
  //   if (isDropdownOpen) {
  //     // Small delay to ensure DOM is ready
  //     setTimeout(updateScrollFades, 0);
  //   }
  // }, [isDropdownOpen, filteredOptions, updateScrollFades]);

  // Scroll selected item into view
  useEffect(() => {
    if (selectedIndex >= 0 && scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const items = container.querySelectorAll('[data-dropdown-item]');
      const selectedItem = items[selectedIndex] as HTMLElement;

      if (selectedItem) {
        selectedItem.scrollIntoView({
          behavior: 'instant',
          block: 'nearest',
          inline: 'nearest',
        });
      }
    }
  }, [selectedIndex]);

  const selectedOption = options.find((option) => option.value === value);

  return (
    <DropdownMenuRoot
      open={isDropdownOpen}
      onOpenChange={handleDropdownOpenChange}
    >
      <DropdownMenuTrigger
        className={classList(
          'flex items-center justify-between w-full px-3 py-2 text-sm bg-transparent rounded-md smoothing-default',
          disabled && 'opacity-50 cursor-not-allowed',
          triggerClassName,
          isDropdownOpen && ['opacity-100']
        )}
        disabled={disabled}
      >
        <span className="truncate">{selectedOption?.label || placeholder}</span>
        <Icons.chevronDown
          size={14}
          className={classList(
            'text-foreground-muted transition-transform',
            isDropdownOpen && 'rotate-180'
          )}
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        ref={dropdownContentRef}
        align="start"
        className={classList('max-w-64 p-0 overflow-hidden', contentClassName)}
        autoFocus={false}
        onCloseAutoFocus={(e) => e.preventDefault()}
        onKeyDown={handleKeyDown}
      >
        {/* Search input */}
        <div className="p-2">
          <div className="relative">
            <Icons.search
              size={14}
              className="absolute left-2 top-1/2 -translate-y-1/2 text-foreground-muted"
            />
            <Input
              ref={searchInputRef}
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-7 h-7 text-xs"
              onKeyDown={handleKeyDown}
            />
          </div>
        </div>

        {/* Options list with scroll fades */}
        <div className="relative">
          {/* Top fade */}
          {showTopFade && (
            <div className="absolute top-0 left-0 right-0 h-6 bg-gradient-to-b from-surface to-transparent pointer-events-none z-10" />
          )}

          {/* Scrollable content */}
          <div
            ref={scrollContainerRef}
            className={classList(
              'overflow-y-auto p-1 pt-0 relative',
              maxHeight
            )}
            onScroll={updateScrollFades}
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <DropdownMenuItem
                  key={option.value}
                  data-dropdown-item
                  textSize="xs"
                  onPointerLeave={(event) => event.preventDefault()}
                  onPointerMove={(event) => event.preventDefault()}
                  onClick={() => handleValueChange(option.value)}
                  className={classList(
                    'py-1.5 mb-1 scroll-my-1 transition-none hover:bg-transparent',
                    selectedIndex === index &&
                      'bg-surface-active hover:bg-surface-active'
                  )}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  {option.description ? (
                    <span className="flex flex-col min-w-0">
                      <span className="truncate">{option.label}</span>
                      <span className="truncate text-foreground-muted">
                        {option.description}
                      </span>
                    </span>
                  ) : (
                    option.label
                  )}
                </DropdownMenuItem>
              ))
            ) : (
              <div className="px-2 py-3 text-xs text-foreground-muted text-center">
                {emptyResult}
              </div>
            )}
          </div>

          {/* Bottom fade */}
          {showBottomFade && (
            <div className="absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-surface to-transparent pointer-events-none z-10" />
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenuRoot>
  );
}
